/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Button,
  Image,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { helper } from '@src/controls/controlHelper';
import { formatTs, parseIdList } from './voucherReminderUtils';

export type VoucherPickerItem = {
  id: number;
  name?: string;
  images?: any;
  partnerId?: number;
  partnerName?: string;
  provider?: string;
  startDate?: number | string | null;
  endDate?: number | string | null;
};

type Props = {
  value?: number[];
  onChange?: (ids: number[]) => void;
  /** Page chứa API list voucher (vd FlashSale get-voucher) */
  voucherPageId?: number;
  voucherApi?: string;
  partnerPageId?: number;
  disabled?: boolean;
};

function firstImage(images: any): string | undefined {
  if (!images) return undefined;
  const arr = Array.isArray(images) ? images : [];
  const first = arr[0];
  if (!first) return undefined;
  if (typeof first === 'string') return first;
  return first?.url || first?.src || undefined;
}

function voucherName(v: any, locale: string) {
  const fromTrans = v?.translations?.find(
    (t: any) => t.language === locale || t.locale === locale
  )?.name;
  return fromTrans || v?.name || `#${v?.id}`;
}

const VoucherReminderVoucherPicker = ({
  value,
  onChange,
  voucherPageId,
  voucherApi = 'get-voucher',
  partnerPageId,
  disabled,
}: Props) => {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, any>) =>
    intl.formatMessage({ id }, values);
  const locale = (intl.locale || 'vi').split('-')[0];

  const selectedIds = parseIdList(value);
  const [modal, setModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<VoucherPickerItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [draftIds, setDraftIds] = useState<number[]>([]);
  const [selectedMap, setSelectedMap] = useState<Record<number, VoucherPickerItem>>(
    {}
  );
  const pageInfoRef = useRef<any>(null);
  const partnerCache = useRef<Record<number, string>>({});

  const resolvePartners = async (list: any[]) => {
    const ids = [
      ...new Set(
        list
          .map((v) => Number(v.partnerId))
          .filter((id) => Number.isFinite(id) && id > 0)
      ),
    ].filter((id) => !partnerCache.current[id]);

    if (!ids.length) return;

    // Prefer provider string từ voucher (không cần partner page)
    list.forEach((v) => {
      const pid = Number(v.partnerId);
      if (
        Number.isFinite(pid) &&
        pid > 0 &&
        !partnerCache.current[pid] &&
        v.provider
      ) {
        partnerCache.current[pid] = String(v.provider);
      }
    });

    const pageId = partnerPageId || voucherPageId;
    if (!pageId) return;

    const missing = ids.filter((id) => !partnerCache.current[id]);
    if (!missing.length) return;

    try {
      const partnerPage = await helper.getPage(pageId);
      if (!partnerPage) return;
      // Ưu tiên API trỏ partner; fallback find
      const apiName =
        partnerPage.apis?.find(
          (a: any) =>
            a.name === 'find' ||
            String(a.url || '').includes('/partner') ||
            a.name === 'get-partner'
        )?.name || partnerPage.apis?.[0]?.name;
      if (!apiName) return;
      const rs: any = await helper.callPageApi(partnerPage, apiName, {
        queryInput: JSON.stringify({ id: missing }),
        limit: missing.length,
        skip: 0,
      });
      (rs?.data?.data || []).forEach((p: any) => {
        if (p?.id != null) {
          partnerCache.current[Number(p.id)] = p.name || `#${p.id}`;
        }
      });
    } catch (e) {
      // ignore — fallback provider / #id
    }
  };

  const mapRow = (v: any): VoucherPickerItem => {
    const partnerId = v.partnerId != null ? Number(v.partnerId) : undefined;
    const partnerName =
      v.partnerInfo?.name ||
      (partnerId ? partnerCache.current[partnerId] : undefined) ||
      v.provider ||
      undefined;
    return {
      id: Number(v.id),
      name: voucherName(v, locale),
      images: v.images,
      partnerId,
      partnerName,
      provider: v.provider,
      startDate: v.startDate,
      endDate: v.endDate,
    };
  };

  const selectedLabel = (id: number, item?: VoucherPickerItem) => {
    const partner =
      item?.partnerName ||
      item?.provider ||
      (item?.partnerId ? partnerCache.current[item.partnerId] : undefined);
    return partner ? `${id} - ${partner}` : `${id}`;
  };

  const loadSelected = async (ids: number[]) => {
    if (!ids.length || !voucherPageId) {
      setSelectedMap({});
      return;
    }
    try {
      if (!pageInfoRef.current) {
        pageInfoRef.current = await helper.getPage(voucherPageId);
      }
      const pageInfo = pageInfoRef.current;
      if (!pageInfo) return;
      const rs: any = await helper.callPageApi(pageInfo, voucherApi, {
        queryInput: JSON.stringify({ id: ids }),
        limit: ids.length,
        skip: 0,
        select: 'id,name,images,partnerId,provider,startDate,endDate,translations',
      });
      const data = rs?.data?.data || [];
      await resolvePartners(data);
      const map: Record<number, VoucherPickerItem> = {};
      data.forEach((v: any) => {
        const row = mapRow(v);
        map[row.id] = row;
      });
      setSelectedMap(map);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadSelected(selectedIds);
  }, [selectedIds.join(','), voucherPageId]);

  const fetchList = async (current = page, nameQ = search) => {
    if (!voucherPageId) {
      message.error(t('voucherReminder.voucherPicker.missingPage'));
      return;
    }
    setLoading(true);
    try {
      if (!pageInfoRef.current) {
        pageInfoRef.current = await helper.getPage(voucherPageId);
      }
      const pageInfo = pageInfoRef.current;
      if (!pageInfo) {
        message.error(t('voucherReminder.voucherPicker.missingPage'));
        return;
      }
      const filter: any = {};
      if (nameQ?.trim()) filter.name = { contains: nameQ.trim() };

      const rs: any = await helper.callPageApi(pageInfo, voucherApi, {
        queryInput: JSON.stringify(filter),
        sort: JSON.stringify([{ id: 'desc' }]),
        limit: pageSize,
        skip: pageSize * (current - 1),
        select: 'id,name,images,partnerId,provider,startDate,endDate,translations',
      });
      const data = rs?.data?.data || [];
      await resolvePartners(data);
      setRows(data.map(mapRow));
      setTotal(Number(rs?.data?.count || 0));
    } catch (e) {
      console.error(e);
      message.error(t('voucherReminder.voucherPicker.loadFail'));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setDraftIds([...selectedIds]);
    setPage(1);
    setSearch('');
    setModal(true);
    fetchList(1, '');
  };

  const confirm = () => {
    onChange?.(draftIds);
    // merge draft rows into selected map
    const map = { ...selectedMap };
    rows.forEach((r) => {
      if (draftIds.includes(r.id)) map[r.id] = r;
    });
    setSelectedMap(map);
    setModal(false);
  };

  const removeId = (id: number) => {
    const next = selectedIds.filter((x) => x !== id);
    onChange?.(next);
    setSelectedMap((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const columns = [
    {
      title: t('voucherReminder.voucherPicker.col.image'),
      dataIndex: 'images',
      width: 90,
      render: (images: any) => {
        const src = firstImage(images);
        return src ? (
          <Image src={src} width={64} height={64} style={{ objectFit: 'cover' }} />
        ) : (
          t('voucherReminder.empty')
        );
      },
    },
    {
      title: t('voucherReminder.voucherPicker.col.name'),
      dataIndex: 'name',
      ellipsis: true,
    },
    {
      title: t('voucherReminder.voucherPicker.col.partner'),
      dataIndex: 'partnerName',
      width: 160,
      ellipsis: true,
      render: (v: string) => v || t('voucherReminder.empty'),
    },
    {
      title: t('voucherReminder.voucherPicker.col.period'),
      dataIndex: 'startDate',
      width: 200,
      render: (_: any, row: VoucherPickerItem) =>
        `${formatTs(row.startDate, intl.locale)} → ${formatTs(
          row.endDate,
          intl.locale
        )}`,
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 8 }}>
        {selectedIds.map((id) => {
          const item = selectedMap[id];
          return (
            <Tag
              key={id}
              closable={!disabled}
              onClose={() => removeId(id)}
              style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 8px' }}
            >
              {item && firstImage(item.images) ? (
                <img
                  src={firstImage(item.images)}
                  alt=""
                  style={{
                    width: 20,
                    height: 20,
                    objectFit: 'cover',
                    marginRight: 6,
                    borderRadius: 2,
                  }}
                />
              ) : null}
              {selectedLabel(id, item)}
            </Tag>
          );
        })}
      </Space>
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={openModal}
        disabled={disabled}
      >
        {t('voucherReminder.voucherPicker.select')}
      </Button>

      <Modal
        title={t('voucherReminder.voucherPicker.title')}
        visible={modal}
        onCancel={() => setModal(false)}
        onOk={confirm}
        okText={t('voucherReminder.voucherPicker.confirm')}
        cancelText={t('voucherReminder.action.cancel')}
        width={900}
        destroyOnClose
        maskClosable={false}
      >
        <Space style={{ marginBottom: 12 }}>
          <Input
            allowClear
            placeholder={t('voucherReminder.voucherPicker.search')}
            prefix={<SearchOutlined />}
            style={{ width: 280 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => {
              setPage(1);
              fetchList(1, search);
            }}
          />
          <Button
            onClick={() => {
              setPage(1);
              fetchList(1, search);
            }}
          >
            {t('voucherReminder.action.filter')}
          </Button>
          <span>
            {t('voucherReminder.voucherPicker.selectedCount', {
              count: draftIds.length,
            })}
          </span>
        </Space>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          columns={columns as any}
          size="small"
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p) => {
              setPage(p);
              fetchList(p, search);
            },
          }}
          rowSelection={{
            selectedRowKeys: draftIds,
            onChange: (keys) => setDraftIds(keys.map((k) => Number(k))),
            preserveSelectedRowKeys: true,
          }}
          scroll={{ y: 420 }}
        />
      </Modal>
    </div>
  );
};

export default VoucherReminderVoucherPicker;
